import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Tabs, Tab, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Alert, List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import Looks4Icon from '@mui/icons-material/Looks4';
import Looks5Icon from '@mui/icons-material/Looks5';
import Looks6Icon from '@mui/icons-material/Looks6';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useIsMobile from '../hooks/useIsMobile';
import {
  TypeQuestion, Operateur, EXTENSIONS_AUTORISEES, LIBELLES_OPERATEURS, LIBELLES_TYPES, LIMITES, OPERATEURS_PAR_TYPE,
  TYPES,
} from '../services/formulairesServices';

// Guide du menu « Formulaires » : comment construire un formulaire, à quoi servent les 9 types de questions, comment
// fonctionnent les questions conditionnelles, puis le cycle joindre → envoyer → suivre. Contenu statique, en français,
// aligné sur les libellés réels du constructeur (FormulaireModeleDialog, QuestionCard, ConditionEditor) et sur les
// règles du moteur (plan §4) — à mettre à jour avec eux. Aucun appel réseau.

interface Props {
  open: boolean;
  onClose: () => void;
  onNouveau?: () => void; // « Créer mon premier formulaire » : ferme le guide et ouvre le constructeur
}

type Onglet = 'creer' | 'types' | 'conditions' | 'envoyer';

// Fiche d'un type de question : usage, réglages propres, ce que voit le client, exemple
interface FicheType {
  type: TypeQuestion;
  usage: string;
  reglages: string;
  client: string;
  exemple: string;
}

const FICHES_TYPES: FicheType[] = [
  {
    type: 'texte',
    usage: 'Une réponse courte sur une ligne : nom, ville, numéro de passeport, profession…',
    reglages: `Format « E-mail » ou « Téléphone » (la saisie est contrôlée), longueur maximale (jusqu'à ${LIMITES.texteMax} caractères).`,
    client: 'Un champ de saisie sur une ligne.',
    exemple: '« Nom de la société qui vous invite »',
  },
  {
    type: 'texte_long',
    usage: 'Une réponse libre de plusieurs lignes : motif du voyage, itinéraire, précisions…',
    reglages: `Longueur maximale (jusqu'à ${LIMITES.texteLongMax} caractères).`,
    client: 'Une zone de texte agrandissable.',
    exemple: '« Décrivez votre programme de voyage »',
  },
  {
    type: 'nombre',
    usage: 'Une valeur numérique : année, nombre de voyageurs, durée en jours…',
    reglages: 'Minimum et maximum (optionnels) : la réponse est refusée hors de ces bornes.',
    client: 'Un champ numérique (la virgule n\'est pas acceptée, seul le point l\'est pour les décimales).',
    exemple: '« Année de votre dernier séjour » (min 1950, max 2030)',
  },
  {
    type: 'date',
    usage: 'Une date : date d\'arrivée, de départ, d\'expiration du passeport…',
    reglages: 'Aucun.',
    client: 'Un sélecteur de date (calendrier du téléphone ou du navigateur).',
    exemple: '« Date d\'arrivée prévue »',
  },
  {
    type: 'oui_non',
    usage: 'Une question fermée à deux réponses. Idéale comme point de départ d\'une condition.',
    reglages: 'Aucun (les deux réponses Oui / Non sont fixes).',
    client: 'Deux boutons radio Oui / Non.',
    exemple: '« Êtes-vous déjà venu dans ce pays ? »',
  },
  {
    type: 'choix',
    usage: 'Une seule réponse parmi une liste que vous définissez.',
    reglages: `Les options (jusqu'à ${LIMITES.options}) : tapez un libellé puis « Ajouter » ; la valeur technique est générée depuis le libellé, une fois pour toutes.`,
    client: 'Des boutons radio jusqu\'à 5 options, une liste déroulante au-delà.',
    exemple: '« But du voyage » : Tourisme / Affaires / Visite familiale',
  },
  {
    type: 'choix_multiple',
    usage: 'Plusieurs réponses possibles parmi une liste.',
    reglages: 'Les options, comme pour le choix unique.',
    client: 'Des cases à cocher.',
    exemple: '« Pays visités ces 5 dernières années » : Chine / Inde / Russie / Aucun',
  },
  {
    type: 'fichier',
    usage: 'Une pièce à joindre : lettre d\'invitation, photo, réservation, passeport…',
    reglages: `Extensions acceptées (${EXTENSIONS_AUTORISEES.map((e) => e.toUpperCase()).join(', ')}) et nombre de fichiers maximum (1 à ${LIMITES.maxFichiers}). Chaque fichier fait 20 Mo au plus.`,
    client: 'Une zone de dépôt (appui ou glisser-déposer) ; le fichier part immédiatement, une miniature s\'affiche. Les pièces arrivent dans les « Pièces jointes » du dossier, nommées form-….',
    exemple: '« Lettre d\'invitation » (PDF, JPG, PNG, 1 fichier)',
  },
  {
    type: 'info',
    usage: 'Un paragraphe explicatif inséré entre les questions : consigne, rappel légal, lien utile. Ce n\'est pas une question : le client n\'y répond pas.',
    reglages: 'Aucun (jamais obligatoire, jamais utilisable dans une condition).',
    client: 'Un encadré d\'information.',
    exemple: '« Les documents doivent être lisibles et en couleur. »',
  },
];

// Exemple lisible d'une condition selon l'opérateur
const EXEMPLES_OPERATEURS: Record<Operateur, string> = {
  egal: '« But du voyage » est égal à « Affaires »',
  different: '« But du voyage » est différent de « Tourisme »',
  parmi: '« But du voyage » est parmi « Affaires », « Visite familiale »',
  pas_parmi: '« Pays visités » n\'est pas parmi « Aucun »',
  contient: '« Pays visités » contient « Chine »',
  contient_pas: '« Pays visités » ne contient pas « Aucun »',
  rempli: '« Lettre d\'invitation » est renseigné',
  vide: '« Société qui vous invite » est vide',
};

const Etape: React.FC<{ icone: React.ReactNode; titre: string; children: React.ReactNode }> = ({ icone, titre, children }) => (
  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
    <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>{icone}</ListItemIcon>
    <ListItemText
      primary={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{titre}</Typography>}
      secondary={<Typography component="div" variant="body2" color="text.primary" sx={{ mt: 0.5 }}>{children}</Typography>}
      disableTypography
    />
  </ListItem>
);

const OngletCreer: React.FC<{ onNouveau?: () => void }> = ({ onNouveau }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 1 }}>
      Un <strong>formulaire</strong> est un modèle réutilisable : vous le construisez une fois ici, puis vous le joignez à autant de
      dossiers que vous voulez. Chaque client reçoit un lien personnel et remplit le formulaire en ligne, sans compte, en une ou
      plusieurs fois.
    </Typography>
    <List disablePadding>
      <Etape icone={<LooksOneIcon color="primary" />} titre="Cliquez sur « Nouveau formulaire »">
        Donnez-lui un <strong>nom</strong> (visible du client dans le mail et en haut de la page), une description interne facultative,
        une <strong>introduction</strong> (affichée au client sur la première étape) et un <strong>message de fin</strong> (affiché après l'envoi).
      </Etape>
      <Etape icone={<LooksTwoIcon color="primary" />} titre="Organisez en sections">
        Une section = une étape à l'écran pour le client (« Votre voyage », « Votre employeur »…). Une première section existe déjà :
        donnez-lui un titre, ajoutez-en d'autres avec « Ajouter une section », réordonnez-les avec les flèches. Une section peut
        elle-même être conditionnelle (voir l'onglet « Questions conditionnelles »).
      </Etape>
      <Etape icone={<Looks3Icon color="primary" />} titre="Ajoutez des questions">
        En bas de chaque section : choisissez le <strong>type</strong>, tapez le libellé, « Ajouter une question ». Sur la carte de la question,
        réglez « Réponse obligatoire », l'aide affichée sous la question et les options propres au type (onglet « Les 9 types de questions »).
        Les flèches déplacent la question dans sa section.
      </Etape>
      <Etape icone={<Looks4Icon color="primary" />} titre="Rendez certaines questions conditionnelles (optionnel)">
        « Afficher seulement si… » masque une question tant qu'une réponse précédente ne correspond pas : la « Lettre d'invitation »
        n'apparaît que si « But du voyage » est égal à « Affaires ».
      </Etape>
      <Etape icone={<Looks5Icon color="primary" />} titre="Vérifiez et testez">
        L'<strong>Aperçu</strong> à droite résume la structure et les conditions en clair. « <strong>Tester le formulaire</strong> » l'ouvre exactement
        comme le client le verra : répondez, voyez les questions apparaître et disparaître, cliquez « Envoyer (test) » pour contrôler les
        obligatoires. Rien n'est enregistré ni envoyé.
      </Etape>
      <Etape icone={<Looks6Icon color="primary" />} titre="Enregistrez et activez">
        « Enregistrer » crée le modèle ; l'interrupteur <strong>Actif</strong> le rend proposable dans « Nouveau dossier » et « Joindre un
        formulaire ». Un modèle inactif reste modifiable mais n'est plus proposé.
      </Etape>
    </List>
    <Alert severity="info" sx={{ mt: 1 }}>
      <strong>Modifier un modèle déjà envoyé ?</strong> Chaque enregistrement qui change les questions crée une nouvelle version. Les
      formulaires déjà envoyés aux clients gardent leur version : rien ne bouge pour eux. La colonne « Dossiers » indique combien
      utilisent une version antérieure. « Dupliquer » crée une copie inactive pour expérimenter sans risque.
    </Alert>
    {onNouveau && (
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Button variant="contained" onClick={onNouveau}>Créer un formulaire</Button>
      </Box>
    )}
  </Box>
);

const OngletTypes: React.FC = () => (
  <Box>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Le <strong>type</strong> d'une question détermine ce que le client voit, ce qui est contrôlé à la saisie et les conditions
      possibles. Il se choisit à l'ajout de la question et peut être changé ensuite sur sa carte (les réglages propres à l'ancien
      type sont alors retirés). Toute question peut être <strong>obligatoire</strong> (sauf le paragraphe d'information) et recevoir
      une <strong>aide</strong> affichée sous le champ.
    </Typography>
    {FICHES_TYPES.map((f, i) => (
      <Box key={f.type} sx={{ mb: 2 }}>
        {i > 0 && <Divider sx={{ mb: 2 }} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{LIBELLES_TYPES[f.type]}</Typography>
          <Chip label={f.type} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        </Box>
        <Typography variant="body2" sx={{ mb: 0.5 }}>{f.usage}</Typography>
        <Typography variant="body2" color="text.secondary"><strong>Réglages :</strong> {f.reglages}</Typography>
        <Typography variant="body2" color="text.secondary"><strong>Côté client :</strong> {f.client}</Typography>
        <Typography variant="body2" color="text.secondary"><strong>Exemple :</strong> {f.exemple}</Typography>
      </Box>
    ))}
    <Alert severity="info">
      Les options d'un choix (unique ou multiple) et l'identifiant technique de chaque question sont générés depuis le libellé la
      première fois, puis figés : vous pouvez corriger un libellé sans rien casser, ni dans les conditions ni dans les réponses déjà
      reçues.
    </Alert>
  </Box>
);

const OngletConditions: React.FC = () => (
  <Box>
    <Typography variant="body2" sx={{ mb: 1 }}>
      Sur la carte d'une question (ou dans l'en-tête d'une section), le bloc « <strong>Afficher seulement si…</strong> » choisit une
      question <strong>précédente</strong>, un <strong>opérateur</strong> et, selon l'opérateur, une <strong>valeur</strong>. La question n'est
      montrée au client — et n'est exigée si elle est obligatoire — que lorsque la condition est vraie. Une seule condition par
      question ; pour enchaîner, faites dépendre une question d'une autre qui est elle-même conditionnelle.
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Les 8 opérateurs</Typography>
    <Table size="small" sx={{ mb: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>Opérateur</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Exemple</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Sens</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(Object.keys(LIBELLES_OPERATEURS) as Operateur[]).map((op) => (
          <TableRow key={op}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{LIBELLES_OPERATEURS[op]}</TableCell>
            <TableCell>{EXEMPLES_OPERATEURS[op]}</TableCell>
            <TableCell>
              {op === 'egal' && 'La réponse est exactement cette valeur.'}
              {op === 'different' && 'La réponse est autre chose (ou n\'est pas encore donnée).'}
              {op === 'parmi' && 'La réponse est l\'une des valeurs cochées.'}
              {op === 'pas_parmi' && 'La réponse n\'est aucune des valeurs cochées (ou n\'est pas encore donnée).'}
              {op === 'contient' && 'Texte : contient ces caractères (majuscules ignorées). Choix multiple : cette option est cochée.'}
              {op === 'contient_pas' && 'L\'inverse de « contient » ; vrai aussi tant que rien n\'est renseigné.'}
              {op === 'rempli' && 'Une réponse (ou un fichier) a été donnée, quelle qu\'elle soit.'}
              {op === 'vide' && 'Aucune réponse pour l\'instant.'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Quel opérateur pour quel type de question visée ?</Typography>
    <Table size="small" sx={{ mb: 2 }}>
      <TableBody>
        {TYPES.filter((t) => t !== 'info').map((t) => (
          <TableRow key={t}>
            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{LIBELLES_TYPES[t]}</TableCell>
            <TableCell>{OPERATEURS_PAR_TYPE[t].map((op) => LIBELLES_OPERATEURS[op]).join(' · ')}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{LIBELLES_TYPES.info}</TableCell>
          <TableCell><em>Jamais utilisable comme cible (le client n'y répond pas).</em></TableCell>
        </TableRow>
      </TableBody>
    </Table>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Les règles à connaître</Typography>
    <List dense disablePadding>
      {[
        'Une question ne peut dépendre que d\'une question placée AVANT elle (même section plus haut, ou section précédente) ; une section ne peut dépendre que d\'une question d\'une section précédente. Le sélecteur ne propose que ces cibles.',
        'Si la question visée est elle-même masquée, la condition est considérée fausse quel que soit l\'opérateur : masquer « But du voyage » masque tout ce qui en dépend, en cascade.',
        'Avec « est différent de », « n\'est pas parmi », « ne contient pas » et « est vide », une cible pas encore renseignée rend la question dépendante VISIBLE. Pour qu\'une question n\'apparaisse qu\'après une réponse précise, préférez « est égal à », « est parmi », « contient » ou « est renseigné ».',
        'Les réponses données à une question devenue invisible sont conservées tant que le client n\'a pas envoyé le formulaire (s\'il change d\'avis, il les retrouve) ; à l\'envoi, elles sont effacées et les fichiers correspondants supprimés.',
        'Supprimer une question ou une option utilisée par une condition : le constructeur vous prévient et retire les conditions devenues orphelines.',
      ].map((t, i) => (
        <ListItem key={i} sx={{ px: 0, alignItems: 'flex-start' }}>
          <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText primary={t} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      ))}
    </List>
    <Alert severity="info" sx={{ mt: 1 }}>
      Exemple complet : « But du voyage » (choix) → « Société qui vous invite » (texte, <em>affichée seulement si But du voyage est égal à
      Affaires</em>) → « Lettre d'invitation » (fichier, <em>affichée seulement si Société qui vous invite est renseigné</em>) → section
      « Votre employeur » (<em>affichée seulement si But du voyage est égal à Affaires</em>). Un client en tourisme ne voit ni la
      société, ni la lettre, ni la section employeur.
    </Alert>
  </Box>
);

const OngletEnvoyer: React.FC = () => (
  <Box>
    <List disablePadding>
      <Etape icone={<LooksOneIcon color="primary" />} titre="Joindre le formulaire à un dossier">
        Deux façons, réservées aux dossiers créés manuellement : dans « <strong>Nouveau dossier</strong> » (menu Demandes), choisissez le
        formulaire dans « Formulaire à joindre » ; ou, sur un dossier existant, dans le panneau « Informations du formulaire », bloc
        <strong> Formulaires</strong> → « Joindre un formulaire ». Plusieurs formulaires peuvent être joints au même dossier.
      </Etape>
      <Etape icone={<LooksTwoIcon color="primary" />} titre="Envoyer le mail au client">
        Le <strong>composeur</strong> s'ouvre pré-rempli (destinataire, objet, texte du modèle « Formulaire à remplir » du menu Mails, avec le
        lien personnel). Relisez, ajustez si besoin (le lien doit rester dans le corps), « Envoyer ». Si vous fermez sans envoyer, le
        formulaire reste au statut <em>créé</em> : envoyez-le plus tard depuis le bloc Formulaires. « Copier le lien » permet aussi de
        le transmettre par un autre canal.
      </Etape>
      <Etape icone={<Looks3Icon color="primary" />} titre="Suivre l'avancement">
        Dans le bloc Formulaires du dossier : <Chip label="créé" size="small" sx={{ mr: 0.5 }} /> pas encore envoyé ·{' '}
        <Chip label="envoyé" size="small" color="primary" sx={{ mr: 0.5 }} /> mail parti, rien de rempli ·{' '}
        <Chip label="en cours" size="small" color="warning" sx={{ mr: 0.5 }} /> le client a commencé (avec le nombre de réponses
        obligatoires manquantes) · <Chip label="soumis" size="small" color="success" sx={{ mr: 0.5 }} /> envoyé par le client ·{' '}
        <Chip label="annulé" size="small" color="error" sx={{ mr: 0.5 }} /> lien désactivé. « Voir les réponses » affiche les réponses
        (survolez une valeur pour la sélectionner et la glisser ailleurs) ; « Journal » retrace chaque événement.
      </Etape>
      <Etape icone={<Looks4Icon color="primary" />} titre="Relancer, rouvrir, annuler">
        « <strong>Relancer</strong> » renvoie le même lien avec un mail de rappel. Après envoi par le client, le formulaire est verrouillé :
        « <strong>Rouvrir</strong> » lui permet de corriger ses réponses (même lien). « <strong>Annuler</strong> » désactive le lien
        définitivement (le client voit « Ce lien n'est plus actif ») ; pour renvoyer quelque chose, joignez un autre formulaire.
        Un dossier archivé désactive aussi ses liens tant qu'il est archivé.
      </Etape>
    </List>
    <Alert severity="info" sx={{ mt: 1 }}>
      Le client n'a pas besoin de compte : il enregistre ses réponses étape par étape (« Enregistrer et reprendre plus tard ») et
      revient avec le même lien, sans limite de temps. Ses pièces jointes arrivent directement dans les « Pièces jointes » du
      dossier (nommées form-…). À l'envoi, il reçoit un mail de confirmation « Formulaire bien reçu » (contact@ en copie cachée) et
      une ligne « Formulaire … soumis » s'ajoute à l'historique du dossier.
    </Alert>
  </Box>
);

const FormulairesGuideDialog: React.FC<Props> = ({ open, onClose, onNouveau }) => {
  const isMobile = useIsMobile();
  const [onglet, setOnglet] = useState<Onglet>('creer');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile} PaperProps={{ sx: isMobile ? {} : { height: '88vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        Guide : créer et envoyer un formulaire
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Fermer">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={onglet}
        onChange={(_, v) => setOnglet(v as Onglet)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
      >
        <Tab value="creer" label="Créer un formulaire" />
        <Tab value="types" label="Les 9 types de questions" />
        <Tab value="conditions" label="Questions conditionnelles" />
        <Tab value="envoyer" label="Envoyer et suivre" />
      </Tabs>
      <DialogContent dividers>
        {onglet === 'creer' && <OngletCreer onNouveau={onNouveau} />}
        {onglet === 'types' && <OngletTypes />}
        {onglet === 'conditions' && <OngletConditions />}
        {onglet === 'envoyer' && <OngletEnvoyer />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormulairesGuideDialog;
